import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  EntitySelectComponent,
  EntitySelectPage
} from '../../../../../../../shared/components/entity-select/entity-select.component';
import { WorkflowVariablePickerComponent } from '../variable-picker/variable-picker.component';
import {
  DirectoryUser,
  UserDirectoryApiService,
  directoryUserLabel
} from '../../../../../../../shared/services/user-directory-api.service';
import { AuthService } from '../../../../../../auth-pages/services/auth.service';
import { ContactoApiService } from '../../../../../../contactos/services/contacto-api.service';
import { Contact } from '../../../../../../contactos/models/contacto.model';
import { EmpresaApiService } from '../../../../../../empresas/services/empresa-api.service';
import { CompanyResponse } from '../../../../../../empresas/models/empresa.model';
import { DealApiService } from '../../../../../../deals/services/deal-api.service';
import { DealResponse } from '../../../../../../deals/models/deal.model';
import {
  WorkflowPropertyOption,
  WorkflowPropertyValueKind,
  WorkflowValueEntityKey,
  findPropertyDescriptor
} from '../../../models/workflow-variable-catalog';
import { WorkflowCatalogValue, WorkflowValueCatalogService } from '../../../services/workflow-value-catalog.service';

/** Forma del editor según el OPERADOR: sin valor, un valor, un rango o una lista. */
type ValueShape = 'none' | 'single' | 'range' | 'list';

/**
 * Editor del VALOR con el que compara una condición, adaptado al operador Y a la propiedad.
 *
 * ## Por qué existe
 *
 * Todos los formularios de condición (IF_ELSE, PROPERTY_CONDITION, MULTI_BRANCH, condición por
 * asociado, filtro del disparador, esperas condicionales, reglas de enrutamiento) pintaban UN
 * input de texto llamado "valor" y lo guardaban siempre en `config.value`. Eso deja tres de los
 * trece operadores imposibles de configurar, y sin ningún aviso:
 *
 * - `BETWEEN` compara contra `from` y `to` (o contra `value` cuando es un array de dos). Con un
 *   texto suelto, `between()` no encuentra extremos y la condición da SIEMPRE falso.
 * - `IN` / `NOT_IN` comparan contra el array `values`. Con un texto suelto, `inList()` lo trata
 *   como un único candidato, así que "a,b,c" solo casaría con el literal "a,b,c": se comportan
 *   exactamente igual que `EQUALS`.
 * - `IS_EMPTY` / `IS_NOT_EMPTY` no comparan con nada, pero el campo se seguía pidiendo.
 *
 * ## Adaptación a la propiedad (INC_CRM_020 (9) inc49/50/52/53/55)
 *
 * El operador ya no es lo único que manda: con `[property]` + `[options]` el editor conoce la
 * FORMA del valor y deja de pedir texto plano cuando puede ofrecer algo mejor:
 *
 * - `CATALOG` (estado, prioridad, categoría, pipeline, ciclo, etapa) &rarr; desplegable con los
 *   valores reales del negocio. Se guarda el id, o el NOMBRE si la propiedad es una `*Name`.
 * - `ENTITY` (empresa, contacto, responsable) &rarr; buscador por nombre, que es lo que el usuario
 *   conoce: nadie sabe de memoria que "Juan Pérez" es el id 55.
 * - `NUMBER` / `DATE` / `BOOLEAN` &rarr; el control nativo correspondiente.
 * - Cualquier otra (atributo personalizado, campo del evento, propiedad escrita a mano) &rarr;
 *   el texto libre de siempre. Nunca se BLOQUEA nada: si el valor guardado no está en el catálogo
 *   se conserva como opción extra en vez de desaparecer en silencio.
 *
 * ## Contrato
 *
 * Emite por `patch` SOLO las claves que hay que mezclar en la condición (`{value}`, `{from,to}`
 * o `{values}`), de forma que sirve igual para una config plana (`patchConfig`), una anidada
 * (`patchNested('condition', …)`) o una fila de un array (`updateIfElse(i, …)`).
 * Los restos de un operador anterior no se borran: el backend ignora `value` cuando hay `values`,
 * y devolver a `EQUALS` tiene que recuperar lo que el usuario había escrito.
 */
@Component({
  selector: 'app-wf-condition-value',
  standalone: true,
  imports: [CommonModule, EntitySelectComponent, WorkflowVariablePickerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @switch (shape()) {
      @case ('none') {
        <p class="rounded-lg bg-gray-50 px-3 py-2 text-[11px] text-gray-500 dark:bg-white/5 dark:text-gray-400">
          Este operador no compara con ningún valor.
        </p>
      }

      @case ('range') {
        <div class="flex items-center gap-2">
          @if (kind() === 'CATALOG') {
            <select [class]="fieldClass" [value]="fromText()" [disabled]="disabled()"
                    (change)="patch.emit({ from: catalogValue(select($event)) })">
              <option value="">desde…</option>
              @for (o of catalogOptions(); track o.label) { <option [value]="optionValue(o)">{{ o.label }}</option> }
              @if (unknownValue(fromText())) { <option [value]="fromText()">{{ fromText() }} (fuera del catálogo)</option> }
            </select>
            <span class="shrink-0 text-[11px] text-gray-400">y</span>
            <select [class]="fieldClass" [value]="toText()" [disabled]="disabled()"
                    (change)="patch.emit({ to: catalogValue(select($event)) })">
              <option value="">hasta…</option>
              @for (o of catalogOptions(); track o.label) { <option [value]="optionValue(o)">{{ o.label }}</option> }
              @if (unknownValue(toText())) { <option [value]="toText()">{{ toText() }} (fuera del catálogo)</option> }
            </select>
          } @else {
            <input [type]="nativeType()" [class]="fieldClass" [value]="fromText()" [disabled]="disabled()" placeholder="desde"
                   (input)="patch.emit({ from: scalar(text($event)) })" />
            <span class="shrink-0 text-[11px] text-gray-400">y</span>
            <input [type]="nativeType()" [class]="fieldClass" [value]="toText()" [disabled]="disabled()" placeholder="hasta"
                   (input)="patch.emit({ to: scalar(text($event)) })" />
          }
        </div>
      }

      @case ('list') {
        @if (kind() === 'CATALOG') {
          <div class="space-y-1 rounded-lg border border-gray-200 p-2 dark:border-gray-800">
            @if (catalogLoading()) {
              <p class="text-[11px] text-gray-400">Cargando valores…</p>
            }
            @for (o of catalogOptions(); track o.label) {
              <label class="flex cursor-pointer items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
                <input type="checkbox" class="h-3.5 w-3.5 rounded border-gray-300 text-brand-500"
                       [checked]="isChecked(optionValue(o))" [disabled]="disabled()"
                       (change)="toggleValue(catalogValue(optionValue(o)), checked($event))" />
                {{ o.label }}
              </label>
            }
            @for (extra of unlistedValues(); track extra) {
              <label class="flex cursor-pointer items-center gap-2 text-xs text-amber-600 dark:text-amber-400">
                <input type="checkbox" class="h-3.5 w-3.5 rounded border-gray-300" [checked]="true" [disabled]="disabled()"
                       (change)="toggleValue(extra, checked($event))" />
                {{ extra }} (fuera del catálogo)
              </label>
            }
          </div>
        } @else if (kind() === 'ENTITY') {
          <div class="space-y-2">
            @if (selectedEntities().length) {
              <div class="flex flex-wrap gap-1.5">
                @for (chip of selectedEntities(); track chip.value) {
                  <span class="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-700 dark:bg-white/10 dark:text-gray-200">
                    {{ chip.label }}
                    <button type="button" class="text-gray-400 hover:text-error-500 disabled:opacity-40"
                            [disabled]="disabled()" (click)="toggleValue(chip.value, false)">
                      <i class="fa-solid fa-xmark text-[10px]"></i>
                    </button>
                  </span>
                }
              </div>
            }
            <app-entity-select
              [value]="null" [displayText]="''" [searchFn]="entitySearch" [labelFn]="entityLabelFn"
              [sublabelFn]="entitySublabelFn" [disabled]="disabled()" [placeholder]="entityPlaceholder()"
              (selected)="onEntityAdded($event)"></app-entity-select>
          </div>
        } @else {
          <input type="text" [class]="fieldClass" [value]="valuesText()" [disabled]="disabled()"
                 placeholder="valor1, valor2, valor3" (input)="onValuesInput($event)" />
          <p class="mt-1 text-[11px] text-gray-400 dark:text-gray-500">Separa los valores con comas.</p>
        }
      }

      @default {
        @if (variableMode()) {
          <!--
            Comparar contra una VARIABLE en vez de un literal: es lo que hace falta para «el
            responsable del ticket es el mismo que el de la empresa». El desplegable sigue siendo
            lo primero que se ofrece; esto es la salida para quien la necesita.
          -->
          <div class="flex items-center gap-1.5">
            <input #variableField type="text" [class]="fieldClass" [value]="valueText()" [disabled]="disabled()"
                   placeholder="{{ '{{' }}contact.email{{ '}}' }}"
                   (input)="patch.emit({ value: text($event) })" />
            <app-wf-variable-picker [target]="variableField" [disabled]="disabled()"
                                    (valueChange)="patch.emit({ value: $event })"></app-wf-variable-picker>
          </div>
        } @else {
        @switch (kind()) {
          @case ('CATALOG') {
            <select [class]="fieldClass" [value]="valueText()" [disabled]="disabled() || catalogLoading()"
                    (change)="patch.emit({ value: catalogValue(select($event)) })">
              <option value="">{{ catalogLoading() ? 'Cargando valores…' : placeholder() }}</option>
              @for (o of catalogOptions(); track o.label) { <option [value]="optionValue(o)">{{ o.label }}</option> }
              @if (unknownValue(valueText())) {
                <option [value]="valueText()">{{ valueText() }} (fuera del catálogo)</option>
              }
            </select>
          }

          @case ('ENTITY') {
            <app-entity-select
              [value]="byName() ? null : entityId()" [displayText]="entityDisplayText()" [searchFn]="entitySearch"
              [labelFn]="entityLabelFn" [sublabelFn]="entitySublabelFn" [disabled]="disabled()"
              [placeholder]="entityPlaceholder()"
              (valueChange)="onEntityValueChange($event)" (selected)="onEntitySelected($event)"></app-entity-select>
          }

          @case ('BOOLEAN') {
            <select [class]="fieldClass" [value]="valueText()" [disabled]="disabled()"
                    (change)="patch.emit({ value: booleanValue(select($event)) })">
              <option value="">Elige un valor…</option>
              <option value="true">Sí</option>
              <option value="false">No</option>
            </select>
          }

          @default {
            <input [type]="nativeType()" [class]="fieldClass" [value]="valueText()" [disabled]="disabled()"
                   [placeholder]="placeholder()" (input)="patch.emit({ value: scalar(text($event)) })" />
            @if (emptyCatalog()) {
              <p class="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                Todavía no hay valores configurados para esta propiedad, así que se escribe a mano.
                Créalos en Configuración para elegirlos de una lista.
              </p>
            }
          }
        }
        }

        @if (offersVariableToggle()) {
          <button type="button" class="mt-1 text-[11px] font-medium text-brand-500 hover:text-brand-600 disabled:opacity-40"
                  [disabled]="disabled()" (click)="toggleVariableMode()">
            {{ variableMode() ? backToValueLabel() : 'Usar una variable' }}
          </button>
        }
      }
    }
  `
})
export class WorkflowConditionValueComponent {
  readonly operator = input<string>('EQUALS');
  readonly value = input<unknown>(null);
  readonly from = input<unknown>(null);
  readonly to = input<unknown>(null);
  readonly values = input<unknown>(null);
  readonly disabled = input<boolean>(false);
  readonly placeholder = input<string>('valor');
  /** Clave de la propiedad que se compara. Vacía = texto libre, como antes. */
  readonly property = input<string>('');
  /** Propiedades del objeto (las mismas que alimentan `app-wf-property-select`). */
  readonly options = input<readonly WorkflowPropertyOption[]>([]);
  readonly patch = output<Record<string, unknown>>();

  private readonly catalogService = inject(WorkflowValueCatalogService);
  private readonly directory = inject(UserDirectoryApiService);
  private readonly auth = inject(AuthService);
  private readonly contactoApi = inject(ContactoApiService);
  private readonly empresaApi = inject(EmpresaApiService);
  private readonly dealApi = inject(DealApiService);

  protected readonly fieldClass =
    'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90';

  /** Descriptor de la propiedad elegida; `undefined` para una propiedad libre. */
  private readonly descriptor = computed(() => findPropertyDescriptor(this.options(), this.property()));

  /** Forma DECLARADA de la propiedad en el catálogo, antes de saber si su lista tiene valores. */
  private readonly declaredKind = computed(() => this.descriptor()?.kind ?? 'TEXT');

  /**
   * Forma REAL con la que se pinta el editor.
   *
   * Una propiedad `CATALOG` cuyo catálogo llega VACÍO —el negocio no ha configurado categorías
   * todavía, o la petición falló— dejaba un `<select>` sin una sola opción: el campo quedaba
   * inservible y no había forma de escribir el valor a mano. Al quedarse sin lista, la propiedad
   * se trata como texto libre, que es exactamente lo que había antes de los desplegables y nunca
   * bloquea la configuración del paso.
   */
  protected readonly kind = computed<WorkflowPropertyValueKind>(() => {
    const declared = this.declaredKind();
    if (declared === 'CATALOG' && this.catalogState() === 'empty') {
      return 'TEXT';
    }
    return declared;
  });

  /**
   * ¿El valor guardado es una variable? Un `{{...}}` guardado hace que el editor abra en modo
   * variable al reabrir el paso, en vez de mostrar un desplegable que no puede representarlo.
   */
  private readonly storedIsVariable = computed(() => /\{\{[^}]+\}\}/.test(this.valueText()));

  /** Elección explícita del usuario; sin ella manda lo que haya guardado. */
  private readonly variableModeOverride = signal<boolean | null>(null);

  protected readonly variableMode = computed(
    () => this.variableModeOverride() ?? this.storedIsVariable()
  );

  /**
   * El interruptor solo aparece donde el editor NO es ya un texto libre: en un texto se puede
   * escribir la variable directamente, así que un botón sobraría.
   */
  protected readonly offersVariableToggle = computed(
    () => this.shape() === 'single' && (this.variableMode() || this.kind() !== 'TEXT')
  );

  /**
   * Volver del modo variable no siempre lleva a una lista: en una propiedad de texto se vuelve a
   * escribir el valor a mano, y llamarlo «elegir de la lista» sería mentir sobre lo que hace.
   */
  protected readonly backToValueLabel = computed(() =>
    this.kind() === 'CATALOG' || this.kind() === 'ENTITY' || this.kind() === 'BOOLEAN'
      ? 'Elegir de la lista'
      : 'Escribir el valor'
  );

  protected toggleVariableMode(): void {
    if (this.disabled()) {
      return;
    }
    const next = !this.variableMode();
    this.variableModeOverride.set(next);
    // Al volver a la lista se limpia el token: dejarlo sería mostrar un desplegable con un valor
    // "fuera del catálogo" que el usuario no eligió.
    if (!next && this.storedIsVariable()) {
      this.patch.emit({ value: '' });
    }
  }

  /** Forma que toma el editor según el operador. */
  protected readonly shape = computed<ValueShape>(() => {
    switch (this.operator().trim().toUpperCase()) {
      case 'IS_EMPTY':
      case 'IS_NOT_EMPTY':
        return 'none';
      case 'BETWEEN':
        return 'range';
      case 'IN':
      case 'NOT_IN':
        return 'list';
      default:
        return 'single';
    }
  });

  /** Tipo del `<input>` nativo cuando no hay lista ni buscador que ofrecer. */
  protected readonly nativeType = computed(() => {
    switch (this.kind()) {
      case 'NUMBER':
        return 'number';
      case 'DATE':
        return 'date';
      default:
        return 'text';
    }
  });

  // ── Valores de catálogo ──────────────────────────────────────────────────────

  private readonly catalogValues = signal<WorkflowCatalogValue[]>([]);
  protected readonly catalogOptions = this.catalogValues.asReadonly();

  /**
   * En qué punto está la carga del catálogo. `loading` importa: sin él, el editor pintaría un
   * input de texto durante el viaje HTTP y lo cambiaría por el desplegable al llegar la lista,
   * robándole el foco al usuario que ya hubiera empezado a escribir.
   */
  private readonly catalogState = signal<'idle' | 'loading' | 'ready' | 'empty'>('idle');
  protected readonly catalogLoading = computed(() => this.catalogState() === 'loading');

  /**
   * ¿La propiedad compara contra el NOMBRE (`statusName`, `companyName`) o contra el id?
   *
   * Vale para las dos formas: en un `CATALOG` decide si el `<option>` guarda el nombre o el id, y
   * en una `ENTITY` hace que el buscador guarde el nombre del registro elegido en vez de su id.
   */
  protected readonly byName = computed(() => this.descriptor()?.byName === true);

  /**
   * La propiedad SÍ tenía un catálogo pero llegó vacío, así que se cayó al texto libre. Se avisa
   * en vez de dejar un input mudo: es exactamente el caso de «Categoría» en un negocio que aún no
   * ha creado ninguna, donde el usuario ve un campo de texto y cree que el desplegable falta.
   */
  protected readonly emptyCatalog = computed(
    () => this.declaredKind() === 'CATALOG' && this.catalogState() === 'empty'
  );

  constructor() {
    // El catálogo solo se pide cuando la propiedad elegida lo necesita, y el servicio lo cachea
    // por sesión: cambiar de fila o reabrir el nodo no repite la llamada.
    effect(() => {
      const catalog = this.descriptor()?.catalog;
      if (this.declaredKind() !== 'CATALOG' || !catalog) {
        this.catalogValues.set([]);
        this.catalogState.set('idle');
        return;
      }
      this.catalogState.set('loading');
      this.catalogService.values(catalog).subscribe({
        next: (list) => {
          this.catalogValues.set(list);
          this.catalogState.set(list.length ? 'ready' : 'empty');
        },
        error: () => {
          this.catalogValues.set([]);
          this.catalogState.set('empty');
        }
      });
    });

    // Nombres de los registros ya guardados (el buscador solo tiene el id al reabrir el nodo).
    effect(() => {
      if (this.kind() !== 'ENTITY' || this.byName()) {
        return;
      }
      const ids = this.shape() === 'list' ? this.valueList().map((v) => Number(v)) : [Number(this.valueText())];
      ids.filter((id) => Number.isFinite(id) && id > 0).forEach((id) => this.resolveEntityLabel(id));
    });
  }

  protected optionValue(option: WorkflowCatalogValue): string {
    return this.byName() ? option.name : String(option.id ?? option.name);
  }

  /** El valor guardado no está entre las opciones → se conserva visible en vez de perderse. */
  protected unknownValue(raw: string): boolean {
    return raw !== '' && this.catalogOptions().length > 0 && !this.catalogOptions().some((o) => this.optionValue(o) === raw);
  }

  /** Lo que se guarda al elegir una opción: el nombre tal cual, o el id como número. */
  protected catalogValue(raw: string): unknown {
    if (raw === '') {
      return '';
    }
    if (this.byName()) {
      return raw;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : raw;
  }

  protected booleanValue(raw: string): unknown {
    return raw === '' ? '' : raw === 'true';
  }

  /** Texto de un input tipado: número real para NUMBER, texto para el resto. */
  protected scalar(raw: string): unknown {
    if (this.kind() === 'NUMBER' && raw.trim() !== '') {
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : raw;
    }
    return raw;
  }

  // ── Entidades (empresa, contacto, responsable) ───────────────────────────────

  private readonly entityLabels = signal<Record<number, string>>({});

  private readonly entityKind = computed<WorkflowValueEntityKey>(() => this.descriptor()?.entity ?? 'CONTACT');

  protected readonly entityId = computed<number | null>(() => {
    const parsed = Number(this.valueText());
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  });

  protected readonly entityDisplayText = computed(() => {
    if (this.byName()) {
      // El valor guardado ya ES el nombre: no hay id que resolver contra el directorio.
      return this.valueText();
    }
    const id = this.entityId();
    if (id == null) {
      return '';
    }
    return this.entityLabels()[id] ?? this.directoryLabel(id) ?? '';
  });

  protected readonly entityPlaceholder = computed(() => {
    switch (this.entityKind()) {
      case 'USER':
        return 'Buscar usuario por nombre o correo…';
      case 'COMPANY':
        return 'Buscar empresa por nombre…';
      case 'DEAL':
        return 'Buscar negocio por nombre…';
      default:
        return 'Buscar contacto por nombre o correo…';
    }
  });

  /** Chips de los registros ya elegidos en un operador de lista. */
  protected readonly selectedEntities = computed(() =>
    this.valueList().map((raw) => {
      const id = Number(raw);
      const label = Number.isFinite(id) ? (this.entityLabels()[id] ?? this.directoryLabel(id)) : null;
      return { value: raw, label: label ?? String(raw) };
    })
  );

  entitySearch = (term: string, page: number): Observable<EntitySelectPage<unknown>> => {
    const businessId = this.auth.getCurrentUser()?.businessId || 1;
    switch (this.entityKind()) {
      case 'USER':
        return this.directory.searchUsers(term, 20).pipe(
          map((list) => ({ items: list as unknown[], totalPages: 1 })),
          catchError(() => of({ items: [] as unknown[], totalPages: 0 }))
        );
      case 'COMPANY':
        return this.empresaApi.searchCompanies({ businessId, searchTerm: term, page, size: 10 }).pipe(
          map((r) => ({ items: r.content as unknown[], totalPages: r.totalPages })),
          catchError(() => of({ items: [] as unknown[], totalPages: 0 }))
        );
      case 'DEAL':
        return this.dealApi.searchDeals({ businessId, searchName: term || undefined, page, size: 10 }).pipe(
          map((r) => ({ items: r.content as unknown[], totalPages: r.totalPages })),
          catchError(() => of({ items: [] as unknown[], totalPages: 0 }))
        );
      default:
        return this.contactoApi.searchContacts({ businessId, searchTerm: term, page, size: 10 }).pipe(
          map((r) => ({ items: r.content as unknown[], totalPages: r.totalPages })),
          catchError(() => of({ items: [] as unknown[], totalPages: 0 }))
        );
    }
  };

  entityLabelFn = (item: unknown): string => this.labelOf(item);
  entitySublabelFn = (item: unknown): string => {
    const record = item as { mail?: string; businessName?: string };
    return record?.mail ?? record?.businessName ?? '';
  };

  /**
   * El buscador emite el id. Cuando la propiedad compara por NOMBRE ese id no sirve, y quien
   * guarda el valor es `onEntitySelected` (que sí recibe el registro completo): aquí se ignora
   * para no pisar el nombre con un número. `EntitySelectComponent` emite siempre los dos eventos,
   * primero `valueChange` y después `selected`, también al limpiar.
   */
  protected onEntityValueChange(id: number | null): void {
    if (this.byName()) {
      return;
    }
    this.patch.emit({ value: id });
  }

  protected onEntitySelected(item: unknown): void {
    if (this.byName()) {
      this.patch.emit({ value: item ? this.labelOf(item) : '' });
      return;
    }
    const id = this.idOf(item);
    if (id != null) {
      this.rememberEntity(id, this.labelOf(item));
    }
  }

  /** En un operador de lista, cada elección AÑADE al array en vez de sustituirlo. */
  protected onEntityAdded(item: unknown): void {
    if (this.disabled() || !item) {
      return;
    }
    if (this.byName()) {
      const label = this.labelOf(item);
      if (label) {
        this.toggleValue(label, true);
      }
      return;
    }
    const id = this.idOf(item);
    if (id == null) {
      return;
    }
    this.rememberEntity(id, this.labelOf(item));
    this.toggleValue(id, true);
  }

  private rememberEntity(id: number, label: string): void {
    if (!label) {
      return;
    }
    this.entityLabels.update((current) => ({ ...current, [id]: label }));
  }

  private idOf(item: unknown): number | null {
    const id = (item as { id?: unknown })?.id;
    return typeof id === 'number' && Number.isFinite(id) ? id : null;
  }

  private labelOf(item: unknown): string {
    if (!item) {
      return '';
    }
    const record = item as {
      id?: number;
      fullname?: string;
      lastname?: string;
      mail?: string;
      name?: string;
      commercialName?: string;
      businessName?: string;
    };
    switch (this.entityKind()) {
      case 'USER':
        return directoryUserLabel(record as DirectoryUser);
      case 'COMPANY':
        return record.commercialName || record.businessName || `Empresa #${record.id}`;
      case 'DEAL':
        return record.name || `Negocio #${record.id}`;
      default:
        return `${record.fullname ?? ''} ${record.lastname ?? ''}`.trim() || record.mail || `Contacto #${record.id}`;
    }
  }

  /** Nombre de un usuario ya conocido por el directorio (caché compartida del CRM). */
  private directoryLabel(id: number): string | null {
    const user = this.directory.knownUsers().get(id);
    return user ? directoryUserLabel(user) : null;
  }

  /**
   * Resuelve el nombre de un id guardado. Los usuarios van por el directorio (que agrupa las
   * peticiones); el resto, por su endpoint de detalle. Un fallo deja el id a la vista, que es
   * mejor que un campo en blanco.
   */
  private resolveEntityLabel(id: number): void {
    if (this.entityLabels()[id]) {
      return;
    }
    if (this.entityKind() === 'USER') {
      this.directory.resolveUsers([id]);
      return;
    }
    const businessId = this.auth.getCurrentUser()?.businessId || 1;
    const request$: Observable<unknown> =
      this.entityKind() === 'COMPANY'
        ? this.empresaApi.getCompanyById(id)
        : this.entityKind() === 'DEAL'
          ? this.dealApi.getDealById(id, businessId)
          : this.contactoApi.getContactById(id);
    request$.subscribe({
      next: (item: unknown) => this.rememberEntity(id, this.labelOf(item as Contact | CompanyResponse | DealResponse)),
      error: () => {}
    });
  }

  // ── Lectura de lo guardado ───────────────────────────────────────────────────

  protected readonly valueText = computed(() => asText(this.value()));

  /**
   * `BETWEEN` admite dos formas guardadas: `from`/`to` sueltos (lo que escribe este editor) y
   * `value` como array de dos, que es lo que acepta el backend como alternativa y lo que puede
   * haber dejado alguien editando el JSON crudo. Se leen ambas para no vaciar el campo al abrir.
   */
  protected readonly fromText = computed(() => asText(this.from() ?? valueAt(this.value(), 0)));
  protected readonly toText = computed(() => asText(this.to() ?? valueAt(this.value(), 1)));

  /** Los valores de un operador de lista, siempre como array (aunque se guardara un texto). */
  private readonly valueList = computed<unknown[]>(() => {
    const raw = this.values();
    if (Array.isArray(raw)) {
      return raw;
    }
    const fallback = raw ?? this.value();
    if (Array.isArray(fallback)) {
      return fallback;
    }
    const text = asText(fallback);
    return text === '' ? [] : text.split(',').map((item) => item.trim()).filter((item) => item !== '');
  });

  protected readonly valuesText = computed(() => this.valueList().map((item) => asText(item)).join(', '));

  /** Valores guardados que ya no existen en el catálogo: se muestran marcados, no se borran. */
  protected readonly unlistedValues = computed(() =>
    this.valueList()
      .map((item) => asText(item))
      .filter((raw) => raw !== '' && !this.catalogOptions().some((o) => this.optionValue(o) === raw))
  );

  protected isChecked(raw: string): boolean {
    return this.valueList().some((item) => asText(item) === raw);
  }

  /** Añade o quita un valor del array de `IN` / `NOT_IN`. */
  protected toggleValue(value: unknown, checked: boolean): void {
    const raw = asText(value);
    const current = this.valueList();
    const next = checked
      ? current.some((item) => asText(item) === raw)
        ? current
        : [...current, value]
      : current.filter((item) => asText(item) !== raw);
    this.patch.emit({ values: next });
  }

  protected onValuesInput(event: Event): void {
    const list = this.text(event)
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item !== '');
    this.patch.emit({ values: list });
  }

  protected text(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }

  protected select(event: Event): string {
    return (event.target as HTMLSelectElement).value;
  }

  protected checked(event: Event): boolean {
    return (event.target as HTMLInputElement).checked;
  }
}

function asText(raw: unknown): string {
  return raw == null ? '' : String(raw);
}

function valueAt(raw: unknown, index: number): unknown {
  return Array.isArray(raw) ? raw[index] : null;
}

/** Claves de comparación de una condición guardada, para reconstruir una fila sin perderlas. */
export interface WorkflowConditionValueParts {
  value: unknown;
  from: unknown;
  to: unknown;
  values: unknown;
}

/**
 * Lee las cuatro claves de comparación de una condición ya guardada.
 *
 * Las filas de IF_ELSE, MULTI_BRANCH, el filtro del disparador y las reglas de enrutamiento se
 * reconstruyen campo a campo desde el JSON en cada `computed`, y el array reescrito se guarda
 * entero: cualquier clave que no se copie aquí se PIERDE al tocar otra fila.
 */
export function readConditionValueParts(raw: Record<string, unknown>): WorkflowConditionValueParts {
  return {
    value: raw['value'] ?? null,
    from: raw['from'] ?? null,
    to: raw['to'] ?? null,
    values: raw['values'] ?? null
  };
}
